import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { leasingApi } from '@/components/App/Leasing/api';
import type { TLeasingCreateDto } from '@/components/App/Leasing/types';
import { ROUTES } from '@/components/Shared/routes/routes';
import { DictTypes } from '@/constants';
import { setDictionaryOptions } from '@/services';
import { handleError } from '@/services/handleError';
import { useAppSelector } from '@/store';

import useLeasingRequestId from '../../../../hooks/useLeasingRequestId';
import type { CreateLeasingProps } from '../form-types';

const useCreateLeasing = () => {
  const navigate = useNavigate();

  const { dealer } = useAppSelector((state) => state.userInfo);
  const { dicts } = useAppSelector((state) => state.dict);

  const { requestId: targetLeasingUuid } = useLeasingRequestId();

  const [createLoading, setCreateLoading] = useState(false);

  const countries = useMemo(
    () =>
      setDictionaryOptions(DictTypes.Countries, dicts)?.map((item) => ({
        value: String(item.value),
        label: String(item.label)
      })) || [],
    [dicts]
  );

  const handleCreate = async (formData: CreateLeasingProps) => {
    setCreateLoading(true);

    let createResponse;
    // Проверка на наличие созданного лизинга.
    // Если при update произойдет ошибка, то мы не будем создавать новый лизинг,
    // но просто обновим имеющийся
    if (!targetLeasingUuid) {
      try {
        createResponse = await leasingApi.createLeasingDraft();
      } catch (error) {
        handleError(error);
        setCreateLoading(false);
        return;
      }
    }

    const preparedData: TLeasingCreateDto = {
      dealerId: dealer?.value || '',

      totalAmount: Number(formData.leasingAmount),
      initialPayment: Number(formData.initialPayment),
      period: Number(formData.leasingPeriod),

      leasingSubjects: formData.vehicles.map((vehicle) => ({
        brand_uuid: vehicle.info?.brand.uuid || '',
        model_uuid: vehicle.info?.model.uuid || '',
        year: Number(vehicle.info?.year) || 0,
        cost: +vehicle.price,
        quantity: +vehicle.count
      })),

      iinOrBin: formData.iin,
      companyName: formData.companyName,
      bankRequisites: formData.bankDetails,
      economicActivityClassification: formData.oked,
      mainActivityCode: Number(formData.jobType),
      registrationCountryCode:
        Number(
          countries.find((country) => country.label === formData.country)?.value
        ) || 0,
      legalAddress: formData.legalAddress,
      propertyTypeCode: Number(formData.typeOfProperty),
      placeOfStateRegistrationCode: Number(formData.registrationPlace),
      numberOfStateRegistration: formData.registrationNumber,
      stateRegistrationDate: formData.registrationDate,
      workStartDate: formData.startWork,
      licenseOrganizationAuthority: formData.license,
      licenseInformation: formData.licenseDescription,
      licenseNumber: formData.licenseNumber,

      clientFullName: formData.fullName,
      email: formData.email,
      contactPhoneNumber: formData.phone,

      firstSupervisorFullName: formData.managerName,
      documentNumber: formData.managerDocumentNumber,
      documentIssueDate: formData.managerDocumentIssueDate,
      documentValidEndDate: formData.managerDocumentEndDate,
      docTypeCode: Number(formData.managerDocument),

      files: {
        balanceSheet: {
          name: formData.balanceSheet?.name || '',
          base64: formData.balanceSheet?.base64 || ''
        },
        profitAndLossStatement: {
          name: formData.statement?.name || '',
          base64: formData.statement?.base64 || ''
        },
        cashFlowStatement: {
          name: formData.financeReport?.name || '',
          base64: formData.financeReport?.base64 || ''
        },
        corporateIncomeTaxStatement: {
          name: formData.fno?.name || '',
          base64: formData.fno?.base64 || ''
        },
        creditHistory: {
          name: formData.creditHistory?.name || '',
          base64: formData.creditHistory?.base64 || ''
        },
        fixedAssetsStatement: {
          name: formData.ocStatement?.name || '',
          base64: formData.ocStatement?.base64 || ''
        },
        additionalFiles:
          formData.additionalDocuments?.map((item) => ({
            name: item.name || '',
            base64: item.base64 || ''
          })) || []
      }
    };

    try {
      const leasingUuid = createResponse?.uuid || targetLeasingUuid;

      await leasingApi.updateLeasing(leasingUuid, preparedData);

      navigate(ROUTES.leasingLlpRequest(leasingUuid), { replace: true });
    } catch (error) {
      handleError(error);
    } finally {
      setCreateLoading(false);
    }
  };

  return { createLoading, handleCreate };
};

export default useCreateLeasing;
